// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getSearchHistory, SearchHistoryItem } from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AutocompleteItemModel,
    AutocompleteModel,
    ProductAutocompleteItemModel,
} from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";

import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";

import { startSearch } from "@insite/client-framework/Store/CommonHandlers/StartSearch";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FancyScroll, { FancyScrollRefProps } from "@steiner/App/Components/Common/FancyScroll";
import { FloatingTooltipStyles } from "@steiner/App/Components/FloatingTooltip/FloatingTooltip";
import FloatingMenuTooltip from "@steiner/App/Components/Menu/FloatingMenuTooltip";
import displaySearchProducts from "@steiner/App/Handlers/ProductList/displaySearchProducts";
import xCommonUpdate from "@steiner/App/Handlers/XNG/Common";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import TextField, { TextFieldProps } from "@steiner/MobiusOverrides/TextField";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@steiner/Overrides/Widgets/Header/AutocompleteCategories";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@steiner/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@steiner/Overrides/Widgets/Header/SearchHistory";
import { xClassNames } from "@steiner/XDL/Utilities";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNG-Changes
        isMobileScreen: state.xCommon.isMobileScreen,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    startSearch,
    // XNG-Changes
    xCommonUpdate,
    displaySearchProducts,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel | null;
    isLoading: boolean;
    clickedProduct: boolean;
    // XNG-Changes
    inputWidth?: number;
    autoCompleteOpen?: boolean;
    categoryProducts?: ProductAutocompleteItemModel[];
}

// XNG-Changes
const DOWN_KEY = "ArrowDown";
const UP_KEY = "ArrowUp";
const ENTER_KEY = "Enter";

export interface SearchInputStyles {
    input?: TextFieldProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    searchPopUpWrapper?: InjectableCss;
    floatingTooltip?: FloatingTooltipStyles;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private autocompleteColumnProductParentMenu = React.createRef<HTMLUListElement | null>();
    private autocompleteColumnCategoryParentMenu = React.createRef<HTMLUListElement | null>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
            // XNG-Changes
            autoCompleteOpen: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                this.setState({ query: "" });

                // XNG-Changes
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
            } else {
                // XNG-Changes
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
        // XNG-Changes
        this.setState({ inputWidth: this.inputWrapperRef.current?.getBoundingClientRect().width });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;

        this.props.startSearch({
            query: this.state.query,
            autocompleteModel: this.state.autoCompleteModel || undefined,
            searchPath: this.props.searchPath,
            goToUrl: this.goToUrl,
            searchHistoryEnabled: this.props.searchHistoryEnabled,
            searchHistoryLimit: this.props.searchHistoryLimit,
        });
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
    };

    // XNG-Changes
    loadAutocomplete = debounce((query: string) => {
        this.setState({ categoryProducts: [] });
        this.props.getAutocompleteModel({
            query,
            onSuccess: (result: any) => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: result,
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    this.updatePopoverVisibility(result);
                }
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        // XNG-Changes
        this.setState({ query: newQuery }, () => {
            if (this.state.query) {
                this.loadAutocomplete(newQuery);
            }
        });

        if (!this.state.autoCompleteModel) {
            return;
        }

        this.updatePopoverVisibility(this.state.autoCompleteModel);
        if (newQuery === "") {
            this.clearSearchQuery();
        }
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.setState({ autoCompleteOpen: true });
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.setState({ autoCompleteOpen: false });
    };

    // XNG-Changes
    isPopoverVisible(results: AutocompleteModel | undefined | null) {
        const hasSearchQuery = this.state.query?.length > 0;
        const queryLength = this.state.query?.length > 2;
        const hasResults =
            results &&
            ((results.categories && results.categories.length > 0) ||
                (results.brands && results.brands.length > 0) ||
                (results.content && results.content.length > 0) ||
                (results.products && results.products.length > 0));

        if (!hasSearchQuery) {
            const searchHistoryItems = getSearchHistory();
            return this.props.searchHistoryEnabled && searchHistoryItems?.length > 0;
        }

        return this.props.autocompleteEnabled && (hasResults || !queryLength || (queryLength && !hasResults));
    }

    // XNG-Changes
    updatePopoverVisibility(results: AutocompleteModel | undefined | null) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        // if (!this.state.query) {
        //     this.showAutocomplete = true;
        // } else {
        //     this.showAutocomplete = false;
        // }
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.doSearch();
                }

                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (
        url: string,
        track = true,
        // XNG-Changes
        autoCompleteResultLength?: number,
    ) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        // XNG-Changes: only when clicking/hitting enter on pdp product from search result
        //  (having 1 product search result only) remove stale autocomplete results
        if (autoCompleteResultLength && autoCompleteResultLength === 1) {
            this.setState({
                // don't use stale results on next search and go to wrong PDP
                autoCompleteModel: undefined,
            });
        }
        this.props.history.push(url);
    };

    // XNG-Changes
    loadCategoryData = (categoryId: string | null) => {
        if (!categoryId) {
            return;
        }

        this.props.displaySearchProducts({
            queryString: this.state.query,
            categoryId,
            onSuccess: (categoryProducts?: ProductAutocompleteItemModel[]) => {
                this.setState({ categoryProducts });
            },
        });
    };

    // XNG-Changes
    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            products,
            brands,
            content,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        const hasProducts = products && products.length > 0;
        return (
            <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    // XNG-Changes
                                    loadCategoryData={this.loadCategoryData}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}
                {/* XNG-Changes */}
                {!isRetailSearchCompletionResults &&
                    ((categories && categories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (content && content.length > 0)) && (
                        <FancyScroll refElement={this.autocompleteColumnCategoryParentMenu} ref={this.fancyScrollRef}>
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper"
                                ref={this.autocompleteColumnCategoryParentMenu as any}
                            >
                                <AutocompleteCategories
                                    categories={categories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    // XNG-Changes
                                    loadCategoryData={this.loadCategoryData}
                                />
                                <AutocompleteBrands
                                    brands={brands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                />
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            </StyledWrapper>
                        </FancyScroll>
                    )}
                {hasProducts && (
                    <FancyScroll refElement={this.autocompleteColumnProductParentMenu} ref={this.fancyScrollRef}>
                        <StyledWrapper
                            {...this.styles.autocompleteColumnProductWrapper}
                            className={xClassNames.append([
                                "autocomplete-column-wrapper",
                                hasProducts ? "has-product-column" : undefined,
                            ])}
                            ref={this.autocompleteColumnProductParentMenu as any}
                        >
                            <AutocompleteProducts
                                // XNG-Changes
                                products={
                                    this.state.categoryProducts?.length
                                        ? (this.state.categoryProducts as any)
                                        : products
                                }
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                extendedStyles={this.styles.autocompleteProductsStyles}
                            />
                        </StyledWrapper>
                    </FancyScroll>
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        if (!this.state.query && this.showAutocomplete) {
            return (
                <StyledWrapper
                    {...styles.searchPopUpWrapper}
                    {...{ width: this.state.inputWidth ? this.state.inputWidth - 2 : 300 }}
                >
                    <SearchHistory
                        focusedQuery={this.state.focusedSearchHistoryQuery}
                        goToUrl={this.goToUrl}
                        clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                    />
                </StyledWrapper>
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };

    // XNG-Changes
    render() {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;

        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{
                        className: "searchClickable",
                        "aria-label": "search",
                        onClick: () => this.doSearch(),
                    }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handlePopoverKeyDown}
                    onFocus={this.handleInputFocus}
                    placeholder={translate("Enter keyword or item #")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    aria-multiline="false"
                    aria-required="false"
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    maxLength={100}
                    clearClickableProps={{
                        clickableProps: {
                            disabled: !this.state.query,
                            onClick: () => {
                                this.clearSearchQuery();
                                this.closeAutocomplete();
                            },
                        },
                    }}
                />
            </StyledWrapper>
        );
        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <FloatingMenuTooltip
                    trigger="manual"
                    open={this.state.autoCompleteOpen}
                    triggerComponent={searchInput}
                    overlay={true}
                    showArrow={false}
                    placement="bottom-start"
                    offset={0}
                    extendedStyles={styles.floatingTooltip}
                    onOpenChange={open => this.setState({ autoCompleteOpen: open })}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
